import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Download, Plus, Trash2 } from "lucide-react";

import { isNotFound } from "@/api/client";
import { IdentityProviderInput, getSamlMetadata, getTenant, updateTenantMetadata } from "@/api/tenants";
import { CopyButton } from "@/components/CopyButton";
import { EmergencyAccounts } from "@/components/EmergencyAccounts";
import { Field, Fieldset, FileInput, Input, Radio, Select } from "@/components/form";
import { MutationError, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { TenantNotFound } from "@/components/TenantNotFound";
import { Button, FormActionBar, FormPlaceholder, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { formatDate } from "@/lib/dates";
import { downloadText } from "@/lib/download";
import { inDays, signingCertificateExpiry } from "@/lib/signingCertificate";
import type { IdentityProvider, IdpRoleSource } from "@/types/api";

/**
 * The company directories of a tenant (SAML federation), and the two values the customer's IT
 * enters on its side. The list is saved as a whole and applied to the tenant's Cognito pool at
 * once; this page is the only one that sends it, so saving anything else never touches the pool.
 */

type Preset = "entra" | "google" | "other";
type MetadataSource = "url" | "file";

// Claim names each directory sends by default. Google's are the attribute names the Google Admin
// SAML app is set up with (spec §9.1: to be confirmed by the spike); every value stays editable.
// ``source``: how each directory usually hands its metadata over. Google Workspace publishes no
// metadata URL, only a file to download from the Google Admin console (spike, 2026-09-25).
const PRESETS: Record<Preset, { label: string; email: string; roles: string; source: MetadataSource }> = {
  entra: {
    label: "Microsoft Entra ID",
    email: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
    roles: "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
    source: "url",
  },
  google: { label: "Google Workspace", email: "email", roles: "roles", source: "file" },
  other: { label: "Other SAML directory", email: "", roles: "", source: "url" },
};

interface Draft {
  /** True once saved: the id is the provider name in Cognito and can no longer change. */
  existing: boolean;
  id: string;
  display_name: string;
  metadata_source: MetadataSource;
  metadata_url: string;
  /** The metadata document itself, as loaded (or as saved); `null` until a file is chosen. */
  metadata_file: string | null;
  /** The name of the file chosen on this page, to show which one will be sent; `null` for a saved one. */
  metadata_file_name: string | null;
  /** A chosen file is being read: until its text is in `metadata_file`, the form cannot be saved. */
  metadata_file_reading: boolean;
  /** Why the chosen file could not be read, shown under the field. */
  metadata_file_error: string | null;
  /** Comma-separated, as typed. */
  email_domains: string;
  role_source: IdpRoleSource;
  email_claim: string;
  roles_claim: string;
}

function toDraft(p: IdentityProvider): Draft {
  return {
    existing: true,
    id: p.id,
    display_name: p.displayName,
    metadata_source: p.metadataFile ? "file" : "url",
    metadata_url: p.metadataUrl ?? "",
    metadata_file: p.metadataFile ?? null,
    metadata_file_name: null,
    metadata_file_reading: false,
    metadata_file_error: null,
    email_domains: p.emailDomains.join(", "),
    role_source: p.roleSource,
    email_claim: p.claims.email,
    roles_claim: p.claims.roles ?? "",
  };
}

function newDraft(preset: Preset): Draft {
  return {
    existing: false,
    id: "",
    display_name: "",
    metadata_source: PRESETS[preset].source,
    metadata_url: "",
    metadata_file: null,
    metadata_file_name: null,
    metadata_file_reading: false,
    metadata_file_error: null,
    email_domains: "",
    role_source: "invitation",
    email_claim: PRESETS[preset].email,
    roles_claim: PRESETS[preset].roles,
  };
}

function toInput(d: Draft): IdentityProviderInput {
  const claims: IdentityProviderInput["claims"] = { email: d.email_claim.trim() };
  if (d.role_source === "directory") claims.roles = d.roles_claim.trim();
  return {
    id: d.id.trim(),
    display_name: d.display_name.trim(),
    protocol: "saml",
    // Exactly one source goes out: the backend refuses both.
    ...(d.metadata_source === "file" ? { metadata_file: d.metadata_file } : { metadata_url: d.metadata_url.trim() }),
    email_domains: d.email_domains
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    role_source: d.role_source,
    claims,
  };
}

const ROLE_SOURCES = [
  { value: "invitation", label: "Invitation — roles are granted in the tenant's console" },
  { value: "directory", label: "Directory — roles come from the directory (<stage>.<role>)" },
];

/** Under a saved directory: when its signing certificate expires, loud once it is 60 days away or past. */
function SigningCertificateStatus({ expiresAt }: { expiresAt: number | null }) {
  const expiry = signingCertificateExpiry(expiresAt);
  switch (expiry.state) {
    case "unknown":
      return <p className="text-sm text-ink-muted">Signing certificate expiry unknown</p>;
    case "ok":
      return <p className="text-sm text-ink-muted">Signing certificate expires on {formatDate(expiry.expiresAt)}</p>;
    case "soon":
      return (
        <Notice tone="warning">
          Signing certificate expires on {formatDate(expiry.expiresAt)} ({inDays(expiry.daysLeft)}). Ask the
          customer's IT for the renewed metadata.
        </Notice>
      );
    case "expired":
      return (
        <Notice tone="danger">
          Signing certificate expired on {formatDate(expiry.expiresAt)}. Ask the customer's IT for the renewed
          metadata.
        </Notice>
      );
  }
}

const PRESET_OPTIONS = (Object.keys(PRESETS) as Preset[]).map((p) => ({ value: p, label: PRESETS[p].label }));

export function TenantIdentityProvidersPage() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  const existing = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId,
  });

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [preset, setPreset] = useState<Preset>("entra");
  const [primed, setPrimed] = useState(false);
  const { dirty, reset: markSaved } = useDirtyValues(drafts, primed);
  const guard = useUnsavedChanges(dirty);
  useEffect(() => {
    if (!existing.data || primed) return;
    setDrafts(existing.data.metadata.identityProviders.map(toDraft));
    setPrimed(true);
  }, [existing.data, primed]);

  const saveMut = useMutation({
    mutationFn: (list: IdentityProviderInput[]) =>
      updateTenantMetadata(token, tenantId, { identity_providers: list }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      markSaved();
      guard.leave(`/tenants/${tenantId}`);
    },
  });

  // Fetched with the operator's own token (the browser has no way to attach it to a plain link), then
  // saved as a file client-side — see ``downloadText``. Only enabled once both fields above are
  // populated: the backend answers 409 otherwise, and the button being there while greyed out would
  // invite a click that can only fail.
  const downloadMut = useMutation({
    mutationFn: () => getSamlMetadata(token, tenantId),
    onSuccess: (xml) => {
      // Rebuilds the filename the backend already put in its Content-Disposition, rather than
      // reading that header: CORS does not expose it to `fetch` unless the API opts in with
      // Access-Control-Expose-Headers, which it doesn't — so the two sides just agree on the pattern.
      downloadText(`pulsestation-${tenantId}-saml-metadata.xml`, "application/samlmetadata+xml", xml);
    },
  });

  function update(index: number, patch: Partial<Draft>) {
    setDrafts((list) => list.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  }

  // Read in the browser and sent as text: the backend stores the document and hands it to Cognito.
  // Until the read settles the draft is marked reading, and the form cannot be saved: a Save in between
  // would send the directory without its document. A failed read keeps what was there (nothing, or the
  // saved file) and says so under the field.
  async function loadMetadataFile(index: number, file: File | null) {
    if (!file) return;
    update(index, { metadata_file_reading: true, metadata_file_error: null });
    try {
      const text = await file.text();
      update(index, { metadata_file: text, metadata_file_name: file.name, metadata_file_reading: false });
    } catch {
      update(index, {
        metadata_file_reading: false,
        metadata_file_error: `Could not read ${file.name}. Choose the file again.`,
      });
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    saveMut.mutate(drafts.map(toInput));
  }

  // A directory in file mode without its document (not chosen yet, still being read, or unreadable).
  const fileMissing = drafts.some(
    (d) => d.metadata_source === "file" && (d.metadata_file_reading || d.metadata_file === null),
  );

  const header = (
    <PageHeader
      back={{ to: `/tenants/${tenantId}`, label: existing.data?.metadata.displayName ?? tenantId }}
      title={`Identity providers · ${tenantId}`}
    />
  );
  if (isLoadingOrPaused(existing)) return <FormPlaceholder header={header} />;
  if (existing.isLoadingError && isNotFound(existing.error)) return <TenantNotFound tenantId={tenantId} width="form" />;
  if (existing.isLoadingError) return <FormPlaceholder header={header} failure={loadFailure(existing)} />;

  const metadata = existing.data!.metadata;

  return (
    <Page width="form">
      {header}

      <form onSubmit={onSubmit}>
        <div className="card space-y-5">
          <Fieldset
            legend="For the customer's IT"
            help="The two values to enter in the SAML application the customer creates in its directory. They are the same for every directory of this tenant."
          >
            <div className="mt-3 space-y-4">
              <Field label="Entity ID">
                <Input mono readOnly value={metadata.samlEntityId ?? "Not available until the tenant's pool exists"} />
              </Field>
              {metadata.samlEntityId && <CopyButton value={metadata.samlEntityId} label="Copy Entity ID" />}
              <Field label="Reply URL">
                <Input mono readOnly value={metadata.samlReplyUrl ?? "Not available until the login domain is active"} />
              </Field>
              {/* One row: in a ``space-y`` stack, two inline buttons side by side would each take the gap. */}
              {metadata.samlReplyUrl && (
                <div className="flex flex-wrap items-center">
                  <CopyButton value={metadata.samlReplyUrl} label="Copy Reply URL" />
                  {metadata.samlEntityId && (
                    <Button
                      variant="secondary"
                      icon={Download}
                      loading={downloadMut.isPending}
                      onClick={() => downloadMut.mutate()}
                    >
                      {downloadMut.isPending ? "Downloading…" : "Download SAML metadata"}
                    </Button>
                  )}
                </div>
              )}
              <MutationError mutation={downloadMut} title="Could not download the SAML metadata" />
            </div>
          </Fieldset>

          <Notice tone="info">
            Directories are created on the tenant's sign-in pool as soon as you save. Offer them on an
            environment from its edit form.
          </Notice>

          {drafts.map((d, index) => (
            <Fieldset
              key={index}
              legend={d.display_name || d.id || "New directory"}
              className="border-t border-line pt-4"
            >
              <div className="mt-3 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" help="Shown to users at sign-in.">
                    <Input
                      value={d.display_name}
                      onChange={(e) => update(index, { display_name: e.target.value })}
                      required
                      maxLength={40}
                    />
                  </Field>
                  <Field label="Identifier" help={d.existing ? "Cannot be changed." : "Lowercase letters, digits and hyphens."}>
                    <Input
                      mono
                      value={d.id}
                      disabled={d.existing}
                      onChange={(e) => update(index, { id: e.target.value })}
                      required
                      maxLength={24}
                    />
                  </Field>
                </div>
                <Fieldset legend="Metadata">
                  <div className="mt-2 flex gap-6">
                    <Radio
                      name={`metadata-source-${index}`}
                      label="Metadata URL"
                      checked={d.metadata_source === "url"}
                      onChange={() => update(index, { metadata_source: "url" })}
                    />
                    <Radio
                      name={`metadata-source-${index}`}
                      label="Metadata file"
                      checked={d.metadata_source === "file"}
                      onChange={() => update(index, { metadata_source: "file" })}
                    />
                  </div>
                </Fieldset>
                {d.metadata_source === "url" ? (
                  <Field label="Metadata document URL" help="Given by the customer's IT (federation metadata document, https).">
                    <Input
                      type="url"
                      value={d.metadata_url}
                      onChange={(e) => update(index, { metadata_url: e.target.value })}
                      required
                    />
                  </Field>
                ) : (
                  <div className="space-y-1">
                    <Field
                      label="Metadata document (XML)"
                      error={d.metadata_file_error ?? undefined}
                      help="The SAML metadata XML the customer's IT downloads from its directory (Google Admin: Download metadata)."
                    >
                      <FileInput
                        accept=".xml,application/xml,text/xml,application/samlmetadata+xml"
                        required={d.metadata_file === null}
                        onFile={(file) => void loadMetadataFile(index, file)}
                      />
                    </Field>
                    {d.metadata_file !== null && (
                      <p className="text-sm text-ink-muted">
                        {d.metadata_file_name ? `Loaded: ${d.metadata_file_name}` : "Metadata file loaded"}
                      </p>
                    )}
                  </div>
                )}
                <Field
                  label="Email domains"
                  help="Comma-separated. This directory can only sign in addresses of these domains."
                >
                  <Input
                    value={d.email_domains}
                    onChange={(e) => update(index, { email_domains: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Roles">
                  <Select
                    value={d.role_source}
                    options={ROLE_SOURCES}
                    onChange={(e) => update(index, { role_source: e.target.value as IdpRoleSource })}
                  />
                </Field>
                <Field label="Email claim">
                  <Input
                    mono
                    value={d.email_claim}
                    onChange={(e) => update(index, { email_claim: e.target.value })}
                    required
                  />
                </Field>
                {d.role_source === "directory" && (
                  <Field label="Roles claim" help="Carries values such as production.admin or staging.viewer.">
                    <Input
                      mono
                      value={d.roles_claim}
                      onChange={(e) => update(index, { roles_claim: e.target.value })}
                      required
                    />
                  </Field>
                )}
                {d.existing && (
                  <SigningCertificateStatus
                    expiresAt={
                      metadata.identityProviders.find((p) => p.id === d.id)?.signingCertificateExpiresAt ?? null
                    }
                  />
                )}
                <Button
                  variant="ghost"
                  icon={Trash2}
                  onClick={() => setDrafts((list) => list.filter((_, i) => i !== index))}
                >
                  Remove this directory
                </Button>
              </div>
            </Fieldset>
          ))}

          <div className="flex items-end gap-3 border-t border-line pt-4">
            <Field label="Directory type">
              <Select value={preset} options={PRESET_OPTIONS} onChange={(e) => setPreset(e.target.value as Preset)} />
            </Field>
            <Button variant="secondary" icon={Plus} onClick={() => setDrafts((list) => [...list, newDraft(preset)])}>
              Add directory
            </Button>
          </div>
        </div>

        <FormActionBar
          dirty={dirty}
          disabled={fileMissing}
          disabledReason="Load the metadata file of each directory in file mode first."
          submitLabel="Save"
          pending={saveMut.isPending}
          onCancel={() => navigate(-1)}
          error={<MutationError mutation={saveMut} title="Could not save the identity providers" />}
        />
      </form>

      {/* Outside the form: these actions are immediate and are not part of what "Save" sends. */}
      <div className="card mt-6">
        <EmergencyAccounts tenantId={tenantId} />
      </div>
    </Page>
  );
}
